import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import writeIcon from '../../assets/write.svg';

function StatItem({ icon, value, label, variant }) {
  return (
    <div className={`analytics-list-row-stat analytics-list-row-stat--${variant}`}>
      <img src={icon} alt="" aria-hidden="true" className="analytics-list-row-stat-icon" />
      <span className="analytics-list-row-stat-text">
        <span className="analytics-list-row-stat-value">{value}</span>
        <span className="analytics-list-row-stat-label">{label}</span>
      </span>
    </div>
  );
}

export function hasActivityScore(score) {
  return (score ?? 0) > 0;
}

const RELATIVE_ACTIVITY_SCORE_HELP =
  'Relative Activity Score is a measure of the number of points scored per user per task in this group, benchmarked against the top performing group.';

const MUTED_BAR_COLOR = { r: 226, g: 232, b: 240 };

function parseHexColor(hex) {
  const normalized = String(hex || '').replace('#', '');
  const value = normalized.length === 3
    ? normalized.split('').map((channel) => channel + channel).join('')
    : normalized;
  return {
    r: parseInt(value.slice(0, 2), 16),
    g: parseInt(value.slice(2, 4), 16),
    b: parseInt(value.slice(4, 6), 16),
  };
}

function mixColors(from, to, amount) {
  const t = Math.max(0, Math.min(1, amount));
  return {
    r: Math.round(from.r + (to.r - from.r) * t),
    g: Math.round(from.g + (to.g - from.g) * t),
    b: Math.round(from.b + (to.b - from.b) * t),
  };
}

function toRgb({ r, g, b }) {
  return `rgb(${r}, ${g}, ${b})`;
}

function getActivityBarFillStyle(score, accentColor) {
  const fill = Math.max(0, Math.min(1, score ?? 0));
  const accent = parseHexColor(accentColor);
  const start = mixColors(MUTED_BAR_COLOR, accent, Math.max(0.12, fill * 0.75));
  const end = mixColors(MUTED_BAR_COLOR, accent, Math.min(1, fill * 0.75 + 0.25));
  return {
    width: `${fill * 100}%`,
    background: `linear-gradient(90deg, ${toRgb(start)} 0%, ${toRgb(end)} 100%)`,
  };
}

export function ActivityScoreBar({ score, color = '#FB7820' }) {
  const fill = Math.max(0, Math.min(1, score ?? 0));
  return (
    <div
      className="analytics-list-row-activity-bar"
      tabIndex={0}
      aria-label={`Relative activity score ${fill.toFixed(2)}`}
    >
      <div className="analytics-list-row-activity-bar-track">
        <div
          className="analytics-list-row-activity-bar-fill"
          style={getActivityBarFillStyle(score, color)}
        />
      </div>
      <span className="analytics-list-row-activity-bar-tooltip" role="tooltip">
        {RELATIVE_ACTIVITY_SCORE_HELP}
      </span>
    </div>
  );
}

export function ActivityRowStats({ reflections }) {
  return (
    <div className="analytics-list-row-meta analytics-list-row-meta--stats">
      <StatItem
        icon={writeIcon}
        value={reflections ?? 0}
        label="reflections"
        variant="reflections"
      />
    </div>
  );
}

function ActivityScoreInline({ score }) {
  return (
    <span className="analytics-list-row-activity-score">
      {(score ?? 0).toFixed(2)}
    </span>
  );
}

function TruncatedGroupTitle({ text }) {
  const labelRef = useRef(null);
  const [isTruncated, setIsTruncated] = useState(false);

  const checkTruncation = () => {
    const el = labelRef.current;
    if (!el) return;
    setIsTruncated(el.scrollWidth > el.clientWidth);
  };

  useLayoutEffect(() => {
    checkTruncation();
  }, [text]);

  useEffect(() => {
    const el = labelRef.current;
    if (!el) return undefined;

    const observer = new ResizeObserver(checkTruncation);
    observer.observe(el);
    return () => observer.disconnect();
  }, [text]);

  return (
    <span
      className={`analytics-list-row-label-wrap${isTruncated ? ' analytics-list-row-label-wrap--truncated' : ''}`}
    >
      <span
        ref={labelRef}
        className="modern-list-row-label analytics-list-row-label"
      >
        {text}
      </span>
      {isTruncated && (
        <span className="analytics-list-row-label-tooltip" role="tooltip">
          {text}
        </span>
      )}
    </span>
  );
}

export function RankedListPanel({
  items,
  getKey,
  getLabel,
  renderMeta,
  onItemClick,
  emptyMessage,
  getActivityScore,
  activityColor = '#FB7820',
}) {
  if (!items.length) {
    return (
      <div className="modern-list-panel">
        <div className="modern-list-empty">{emptyMessage}</div>
      </div>
    );
  }

  return (
    <div className="analytics-ranked-list-wrap">
      <div
        className="analytics-ranked-axis"
        style={{ '--list-rows': items.length }}
        aria-hidden="true"
      >
        <span className="analytics-ranked-axis-label analytics-ranked-axis-label--top">Most Active</span>
        {items.length > 1 && <span className="analytics-ranked-axis-line" />}
        {items.length > 1 && (
          <span className="analytics-ranked-axis-label analytics-ranked-axis-label--bottom">Least Active</span>
        )}
      </div>
      <div className="modern-list-panel analytics-ranked-list-panel">
        {items.map((item) => (
          <div
            key={getKey(item)}
            className={`modern-list-row${onItemClick ? ' modern-list-row-clickable' : ''}`}
            onClick={onItemClick ? () => onItemClick(item) : undefined}
          >
            <div className="analytics-list-row-leading">
              <TruncatedGroupTitle text={getLabel(item)} />
              {getActivityScore && (
                <div className="analytics-list-row-activity">
                  <div className="analytics-list-row-activity-meter">
                    <ActivityScoreBar score={getActivityScore(item)} color={activityColor} />
                    <ActivityScoreInline score={getActivityScore(item)} />
                  </div>
                </div>
              )}
            </div>
            {renderMeta?.(item)}
          </div>
        ))}
      </div>
    </div>
  );
}
